'use client'

import {useEffect, useState} from 'react'

import AssignRolesModal from '@/app/users/AssignRolesModal'
import {closeNotif, startNotif} from '@/components/DynamicForm/util/notifier'
import Modal from '@/components/Modal/Modal'
import Splash from '@/components/splash/Splash'
import {Button} from '@/components/UiElements'
import Alert from '@/components/UiElements/Alert'
import deleteUserRolesV2 from '@/services/UserService/deleteUserRolesV2'
import getUserRoles from '@/services/UserService/getUserRoles'
import {Role} from '@/types/role'
import {User} from '@/types/user'
import {getErrorMessage} from '@/utils/errorMessages'
import notifyError from '@/utils/notifyError'
import UserRelationBadge from '@/app/users/UserRelationBadge'

type UserRolesListProps = {
    user: User
}

const isSameRole = (firstRole: Role, secondRole: Role) => (
    firstRole.id === secondRole.id || firstRole.name === secondRole.name
)

const UserRolesList = ({user}: UserRolesListProps) => {
    const [userRoles, setUserRoles] = useState<Role[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [removingRoleKey, setRemovingRoleKey] = useState<string | null>(null)
    const [isAssignRolesModalOpen, setIsAssignRolesModalOpen] = useState(false)

    useEffect(() => {
        let cancelled = false

        const loadUserRoles = async () => {
            setIsLoading(true)

            try {
                const result = await getUserRoles(user.auth0_user_id)
                if (cancelled) return

                setUserRoles(Array.isArray(result) ? result : result?.roles || [])
            } catch (error) {
                if (!cancelled) {
                    setUserRoles([])
                    notifyError(error, 'Unable to load user roles.')
                }
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        void loadUserRoles()

        return () => {
            cancelled = true
        }
    }, [user.auth0_user_id])

    const handleRolesAssigned = (assignedRoles: Role[]) => {
        setUserRoles(currentRoles => [
            ...currentRoles,
            ...assignedRoles.filter(role => (
                !currentRoles.some(currentRole => isSameRole(currentRole, role))
            )),
        ])
    }

    const removeRole = async (role: Role) => {
        if (removingRoleKey) return

        setRemovingRoleKey(role.id || role.name)
        const notificationId = startNotif('Removing role...')

        try {
            await deleteUserRolesV2({
                email: user.email,
                roles: [role.name],
            })
            setUserRoles(currentRoles => (
                currentRoles.filter(currentRole => !isSameRole(currentRole, role))
            ))
            closeNotif(notificationId, 'Role removed successfully.')
        } catch (error) {
            closeNotif(
                notificationId,
                getErrorMessage(error, 'Failed to remove role.'),
                {type: 'error'},
            )
        } finally {
            setRemovingRoleKey(null)
        }
    }

    const handleRemoveRole = (role: Role) => {
        Alert.showAlert({
            title: 'Remove Role',
            message: (
                <div className="flex flex-col items-center justify-center">
                    Are you sure you want to remove the role &quot;{role.name}&quot; from this user?
                </div>
            ),
            isCancelable: true,
            button: 'Remove Role',
            danger: true,
            callBack: (confirmed) => {
                if (confirmed) void removeRole(role)
            },
        })
    }

    return (
        <section>
            {isLoading && <Splash text="Loading user roles..." />}

            <div className="mb-md">
                <p className="text-title-md font-medium text-gray-900">Roles</p>
            </div>

            {!isLoading && (
                <ul className="flex flex-wrap gap-sm">
                    {userRoles.map(role => (
                        <li key={role.id || role.name}>
                            <UserRelationBadge
                                title={role.name}
                                description={role.description}
                                onRemove={() => handleRemoveRole(role)}
                                disabled={Boolean(removingRoleKey)}
                            />
                        </li>
                    ))}
                    <li>
                        <Button
                            size="sm"
                            type="alt"
                            icon="+"
                            className="!h-[64px] !w-[64px] !min-w-[64px] !p-0"
                            onClick={() => setIsAssignRolesModalOpen(true)}
                        />
                    </li>
                </ul>
            )}

            {!isLoading && userRoles.length === 0 && (
                <div className="mt-sm text-text-md text-gray-400">
                    No roles are assigned to this user.
                </div>
            )}

            <Modal
                isOpen={isAssignRolesModalOpen}
                onClose={() => setIsAssignRolesModalOpen(false)}
                title="Assign Roles"
                closeOnOutsideClick={false}
            >
                {isAssignRolesModalOpen && (
                    <AssignRolesModal
                        user={user}
                        assignedRoles={userRoles}
                        onClose={() => setIsAssignRolesModalOpen(false)}
                        onRolesAssigned={handleRolesAssigned}
                    />
                )}
            </Modal>
        </section>
    )
}

export default UserRolesList
